/**
 * One register, on screen exactly as it comes out of the workbook — and
 * editable in place.
 *
 * The reference is the book companies already keep these in — docs/Registers
 * under Cos Act-2013_SHREE NAIVEDYA.xlsx — and this renders the same document:
 * the centred form number, register name and bracketed citation; the identity
 * lines; the "(TO BE MAINTAINED SEPARATELY FOR EACH…)" note; the header block;
 * each folio's details as boxed panels side by side; the prescribed table with
 * its merged two-row header and (1)…(n) numbering; the footnotes; and the
 * Place / Date / signature block. Column letters run across the top and the
 * rows are numbered, so "row 14" means the same thing here as in the file.
 *
 * Every value the company owns is typed where it prints. That is the whole
 * point of keeping the document and the editing surface as one thing: a person
 * maintaining a register is looking at the form, and asking them to hold the
 * form in their head while filling a list of labelled boxes somewhere else is
 * how a book ends up with the right values in the wrong columns. Editing saves
 * on blur, and only when the value actually changed.
 *
 * Read-only access renders the same sheet with static cells, which is the
 * document an auditor or a diligence reviewer wants anyway.
 *
 * Kept in step with backend/services/registerExport.js: same order, same
 * merges, same blank ruled rows on an empty form.
 */

import { useState, useEffect, useRef } from 'react';
import { TbTrash, TbPlus, TbLock, TbLockOpen } from 'react-icons/tb';

/* ── Sheet chrome ────────────────────────────────────────────────────────── */

const TYPE_WIDTH = { date: 110, number: 114, money: 124, text: 150, textarea: 172 };
const ROW_HEADER_WIDTH = 52;

const widthOf = (column) => (column.key === 'srNo' ? 66 : TYPE_WIDTH[column.type] || TYPE_WIDTH.text);

const RULE = 'border border-[#ddd6c6]';
const CELL = `${RULE} px-2 py-[6px] text-[12px] text-[#08111f] align-top leading-snug`;
const HEAD = `${RULE} bg-[#f4f1e9] text-[10px] font-bold text-[#8b8371] text-center py-1 select-none`;
const BAND = `${RULE} bg-[#f4f1e9] px-2 py-[6px] text-[11px] font-bold text-[#4a4335] text-center align-middle leading-tight`;

const INPUT = 'w-full bg-transparent outline-none text-[12px] text-[#08111f] '
  + 'placeholder:text-[#c4bbaa] focus:bg-white focus:shadow-[inset_0_0_0_2px_rgba(20,95,95,0.35)] px-1 py-0.5 rounded-[2px]';

/** Column letters: B, C, … Z, AA — the form starts at B, as in the book. */
function columnLetter(index) {
  let n = index + 1; // 0 → B, the workbook's first content column
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/**
 * One editable value, wherever it sits on the form — a header line, a line
 * inside a folio panel, a cell of the prescribed table.
 *
 * Local state while focused so typing is not fighting the server round-trip,
 * committed on blur and only when changed, following the server's value again
 * once it lands — a save elsewhere in a row recomputes the balances beneath it.
 */
function SheetInput({ value, type, numeric, readOnly, onCommit, placeholder = '', className = '' }) {
  const [draft, setDraft] = useState(value ?? '');
  const dirty = useRef(false);

  useEffect(() => {
    if (!dirty.current) setDraft(value ?? '');
  }, [value]);

  if (readOnly) {
    return (
      <div className={`px-1 py-0.5 ${numeric ? 'text-right tabular-nums' : ''} ${className}`}>
        {value || (placeholder ? <span className="text-[#a39a86]">{placeholder}</span> : '')}
      </div>
    );
  }

  const commit = () => {
    dirty.current = false;
    if ((draft ?? '') !== (value ?? '')) onCommit(draft);
  };

  const shared = {
    value: draft,
    onChange: (e) => { dirty.current = true; setDraft(e.target.value); },
    onBlur: commit,
  };

  if (type === 'textarea') {
    return <textarea rows={1} className={`${INPUT} resize-none leading-snug ${className}`} {...shared} />;
  }

  return (
    <input
      type="text"
      inputMode={numeric ? 'decimal' : undefined}
      // Dates are typed, not picked: a register transcribes what was recorded,
      // and "1/10/25" from an old paper book must survive being entered as-is.
      placeholder={placeholder || (type === 'date' ? 'DD/MM/YYYY' : '')}
      className={`${INPUT} ${numeric ? 'text-right tabular-nums' : ''} ${className}`}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      {...shared}
    />
  );
}

/**
 * An action that belongs beside a row rather than in the form: delete, add.
 *
 * Drawn in its own colour rather than as grey chrome. A muted icon on a tinted
 * gutter was there but not visible — a control you have to hunt for is one
 * people work around, and on a register the workaround is leaving a wrong row
 * in the book.
 */
const IconButton = ({ onClick, title, danger, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`inline-flex items-center justify-center rounded p-[3px] border transition-colors
      ${danger
    ? 'text-[#b3261e] border-[#eec9c3] bg-[#fdf1ef] hover:bg-[#b3261e] hover:text-white hover:border-[#b3261e]'
    : 'text-[#145f5f] border-[#cfe0dd] bg-[#f2f7f6] hover:bg-[#145f5f] hover:text-white'}`}
  >
    {children}
  </button>
);

/* ── Building the rows ───────────────────────────────────────────────────── */

/**
 * Collapse `group` into header spans. Only CONSECUTIVE columns merge — MGT-1
 * has two separate "Distinctive numbers" groups that must stay two groups.
 * Mirrors registerSchemas.headerSpans on the server.
 */
function headerSpans(columns) {
  const spans = [];
  (columns || []).forEach((col, i) => {
    const prev = spans[spans.length - 1];
    if (col.group && prev && prev.grouped && prev.label === col.group && prev.start + prev.span === i) {
      prev.span += 1;
      return;
    }
    spans.push({ label: col.group || col.label, span: 1, start: i, grouped: Boolean(col.group) });
  });
  return spans;
}

const valueOf = (map, key) => (map && map[key] ? String(map[key]) : '');

/**
 * The sheet as data: an array of rows, each an array of cells. A cell carries
 * how many columns it spans, how it is drawn, and either text, an editable
 * field or a node — which is all a faithful rendering of these forms needs, and
 * keeps the layout below readable as the form rather than as markup.
 */
function buildRows(definition, register, canEdit, on, width) {
  const rows = [];
  // A row that continues a rowSpan header already has those columns occupied,
  // so it must not be padded out to the full width.
  const push = (cells, { noPad = false, action = null } = {}) => {
    if (noPad) cells.noPad = true;
    // Deleting a row belongs in the row's own number gutter, which is pinned to
    // the left edge. A trailing action column sat off the right-hand end of a
    // twenty-column form: to delete the row in front of you, you had to scroll
    // away from it first.
    if (action) cells.action = action;
    rows.push(cells);
  };
  const full = (content, className = '') => push([{ ...content, span: width, className }]);
  // A blank line is ruled cell by cell, not as one merged band — the empty rows
  // of a spreadsheet still show their gridlines.
  const blank = () => push(Array.from({ length: width }, () => ({ text: '', span: 1 })));

  const labelSpan = Math.min(2, width - 1);
  const banner = 'text-center font-bold';
  const panelBox = 'bg-[#fcfbf7]';

  full({ text: definition.formLabel }, banner);
  full({ text: definition.title }, banner);
  full({ text: definition.citation }, 'text-center text-[11px] text-[#5c5445]');

  /** "Label: value", the value editable where the company owns it. */
  const headerLine = (field) => push([
    { text: `${field.label}:`, span: labelSpan, className: 'font-bold whitespace-nowrap' },
    {
      span: width - labelSpan,
      className: 'font-bold',
      field: {
        value: valueOf(register.header, field.key),
        type: field.type,
        onCommit: (v) => on.saveHeader({ [field.key]: v }),
      },
    },
  ]);

  for (const field of definition.identityFields || []) headerLine(field);
  if (definition.note) full({ text: definition.note }, 'text-center font-bold text-[#8a6d2f]');
  for (const field of definition.header || []) headerLine(field);

  blank();

  /** A folio's details panel: its title, then one editable line per field. */
  const panel = (p, details, onDetail, editable) => (
    // Keyed on the panel's title: panels are rendered as a list where a folio's
    // details run down the right-hand box.
    <div key={p.title} className="space-y-0.5">
      <div className="font-bold text-[11px] uppercase tracking-[0.4px] text-[#4a4335]">{p.title}</div>
      {p.fields.map((field) => (
        <div key={field.key} className="flex items-baseline gap-1.5">
          <span
            className={`shrink-0 text-[11px] ${field.nonStatutory ? 'text-[#a39a86] italic' : 'text-[#5c5445]'}`}
            title={field.nonStatutory ? 'Kept on screen to tell folios apart; not printed on the form.' : undefined}
          >
            {field.label} :
          </span>
          <SheetInput
            value={valueOf(details, field.key)}
            type={field.type}
            readOnly={!editable}
            onCommit={(v) => onDetail({ [field.key]: v })}
            className="flex-1 min-w-0 border-b border-dotted border-[#ddd6c6]"
          />
        </div>
      ))}
    </div>
  );

  /** The two-row header plus the statutory numbering row. */
  const tableHeader = (columns) => {
    const spans = headerSpans(columns);
    const hasSubRow = spans.some((s) => s.grouped);

    push(spans.map((s) => ({
      text: s.label,
      span: s.span,
      className: BAND,
      raw: true,
      rowSpan: !s.grouped && hasSubRow ? 2 : 1,
    })));

    if (hasSubRow) {
      const sub = [];
      for (const s of spans) {
        if (!s.grouped) continue;
        for (let i = 0; i < s.span; i += 1) {
          sub.push({ text: columns[s.start + i].label, span: 1, className: BAND, raw: true });
        }
      }
      push(sub, { noPad: true });
    }

    if (columns.some((c) => c.no)) {
      push(columns.map((c) => ({
        text: c.no || '',
        span: 1,
        raw: true,
        className: `${RULE} px-2 py-[3px] text-[11px] text-center text-[#7a7060] bg-[#faf8f3]`,
      })));
    }
  };

  /**
   * One entry of a table. A computed column is kept by the register, shown
   * shaded and read-only, with the lock that lets a company take it over by
   * hand — an opening balance carried in from a paper book is legitimate, but
   * it is never silent.
   */
  const dataRow = (columns, row, onUpdate, onDelete, onOverride) => push(
    columns.map((column) => {
      const overridden = (row.overrides || []).includes(column.key);
      const derived = Boolean(column.computed) && !overridden;
      return {
        span: 1,
        className: derived ? 'bg-[#f7f5ef]' : '',
        field: {
          value: valueOf(row.data, column.key),
          type: column.type,
          numeric: column.type === 'number' || column.type === 'money',
          readOnly: derived || !canEdit,
          onCommit: (v) => onUpdate({ [column.key]: v }),
        },
        lock: column.computed && canEdit
          ? { derived, onClick: () => onOverride(column.key) }
          : null,
      };
    }),
    {
      action: canEdit
        ? <IconButton danger title="Delete this entry" onClick={onDelete}><TbTrash size={14} /></IconButton>
        : null,
    },
  );

  const ruledRows = (columns, count) => {
    for (let i = 0; i < count; i += 1) push(columns.map(() => ({ text: '', span: 1 })));
  };

  const addRow = (text, onClick) => push([{
    span: width,
    raw: true,
    className: `${RULE} p-0`,
    node: (
      <button
        type="button"
        onClick={onClick}
        className="sticky left-0 flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-[#145f5f] hover:text-[#08111f]"
      >
        <TbPlus size={13} /> {text}
      </button>
    ),
  }]);

  if (definition.layout === 'folio') {
    const columns = definition.folio.columns;
    const entity = definition.folio.entityLabel.toLowerCase();
    const panels = definition.folio.panels || [];
    const instructions = panels.filter((p) => /^instructions/i.test(p.title));
    const [first, ...others] = panels.filter((p) => !instructions.includes(p));

    // An unused folio register still shows one blank folio: the form is what
    // the company must keep, whether or not anyone is on it yet.
    const folios = register.folios.length
      ? register.folios
      : [{ id: null, folioNo: '', details: {}, entries: [] }];

    for (const folio of folios) {
      const onDetail = (patch) => folio.id && on.updateFolio(folio.id, { details: patch });

      push([
        { text: definition.folio.idLabel, span: labelSpan, className: 'font-bold whitespace-nowrap' },
        {
          span: width - labelSpan,
          className: 'font-bold',
          // The folio number and the way to remove the whole record sit
          // together, pinned near the left edge — a member's folio runs to
          // dozens of rows, so "remove this member" has to be findable from the
          // top of it rather than at the far right of a 23-column table.
          node: (
            <div className="flex items-center gap-4">
              <div className="w-[180px] shrink-0">
                <SheetInput
                  value={folio.folioNo || ''}
                  readOnly={!canEdit || !folio.id}
                  onCommit={(v) => on.updateFolio(folio.id, { folioNo: v })}
                />
              </div>
              {canEdit && folio.id && (
                <button
                  type="button"
                  onClick={() => on.deleteFolio(folio.id)}
                  className="inline-flex items-center gap-1 rounded-md border border-[#eec9c3] bg-[#fdf1ef]
                             px-2 py-[3px] text-[11px] font-semibold text-[#b3261e]
                             hover:bg-[#b3261e] hover:text-white hover:border-[#b3261e] transition-colors"
                >
                  <TbTrash size={12} />
                  Delete this {entity}
                </button>
              )}
            </div>
          ),
        },
      ], {
        action: canEdit && folio.id
          ? (
            <IconButton danger title={`Delete this ${entity}`} onClick={() => on.deleteFolio(folio.id)}>
              <TbTrash size={14} />
            </IconButton>
          )
          : null,
      });

      if (first && others.length) {
        const split = Math.max(Math.floor(width / 2), 1);
        push([
          { node: panel(first, folio.details, onDetail, canEdit && Boolean(folio.id)), span: split, className: panelBox },
          {
            node: <div className="space-y-3">{others.map((p) => panel(p, folio.details, onDetail, canEdit && Boolean(folio.id)))}</div>,
            span: width - split,
            className: panelBox,
          },
        ]);
      } else if (first) {
        full({ node: panel(first, folio.details, onDetail, canEdit && Boolean(folio.id)) }, panelBox);
      }

      for (const p of instructions) full({ node: panel(p, folio.details, onDetail, canEdit && Boolean(folio.id)) }, panelBox);

      if (definition.folio.tableTitle) full({ text: definition.folio.tableTitle }, 'font-bold');
      tableHeader(columns);

      for (const entry of folio.entries) {
        dataRow(
          columns,
          entry,
          (patch) => on.updateFolioRow(folio.id, entry, patch),
          () => on.deleteFolioRow(folio.id, entry),
          (key) => on.toggleFolioOverride(folio.id, entry, key),
        );
      }
      ruledRows(columns, Math.max(3 - folio.entries.length, 0));
      if (canEdit && folio.id) addRow('Add entry', () => on.addFolioRow(folio.id));
      blank();
    }

    if (canEdit) {
      addRow(`Add ${entity}`, on.addFolio);
      blank();
    }
  } else {
    for (const table of definition.tables || []) {
      if (table.title) {
        full({ text: table.title }, 'font-bold');
        blank();
      }
      tableHeader(table.columns);

      const entries = register.entries.filter((e) => (e.table || 'main') === table.key);
      for (const entry of entries) {
        dataRow(
          table.columns,
          entry,
          (patch) => on.updateRow(entry, patch),
          () => on.deleteRow(entry),
          (key) => on.toggleOverride(entry, key),
        );
      }
      ruledRows(table.columns, Math.max(5 - entries.length, 0));
      if (canEdit) addRow('Add entry', () => on.addRow(table.key));
      blank();
    }
  }

  for (const note of definition.footnotes || []) full({ text: note }, 'text-[11px] text-[#5c5445]');

  blank();

  const footer = register.footer || {};
  const right = Math.max(width - 3, 1);
  /**
   * The signature block. Place and Date used to sit on the left of these two
   * rows and have been removed: Form MGT-1 does not prescribe them, and a
   * register is a standing record rather than a document executed on a date —
   * so a date printed at its foot says nothing true about the entries above it.
   * The stored values are left alone rather than deleted, so nothing is lost if
   * they are ever wanted back.
   */
  const footerRow = (key, placeholder) => push([
    { text: '', span: right, className: 'whitespace-nowrap' },
    {
      span: width - right,
      className: 'font-bold',
      field: {
        value: footer[key] || '',
        readOnly: !canEdit,
        placeholder,
        onCommit: (v) => on.saveFooter({ ...footer, [key]: v }),
      },
    },
  ]);

  footerRow('signatoryName', 'Signature');
  footerRow('signatoryDesignation', definition.signatoryLine);

  return rows;
}

/* ── The sheet ───────────────────────────────────────────────────────────── */

export default function RegisterSheet({ definition, register, canEdit = false, on = {} }) {
  const columns = definition.layout === 'folio'
    ? definition.folio.columns
    : (definition.tables || []).reduce(
      (widest, t) => (t.columns.length > widest.length ? t.columns : widest),
      [],
    );

  const width = Math.max(columns.length, 2);
  const rows = buildRows(definition, register, canEdit, on, width);

  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="border-collapse" style={{ minWidth: '100%' }}>
          <colgroup>
            <col style={{ width: ROW_HEADER_WIDTH }} />
            {Array.from({ length: width }, (unused, i) => (
              <col key={i} style={{ width: widthOf(columns[i] || { type: 'text' }) }} />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th className={`${HEAD} sticky left-0 z-20`} />
              {Array.from({ length: width }, (unused, i) => (
                <th key={i} className={HEAD}>{columnLetter(i)}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((cells, r) => {
              // A row that does not reach the full width is padded, so MBP-4's
              // five-column Part B does not leave the sheet ragged.
              const filled = cells.noPad ? width : cells.reduce((n, c) => n + (c.span || 1), 0);

              return (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={r} className="group hover:bg-[#f6f3ea]">
                  {/* The row number, and — on the row you are pointing at — the
                      way to delete it, in the one column that never scrolls
                      out of view. */}
                  <th scope="row" className={`${HEAD} sticky left-0 z-10 font-normal tabular-nums p-0`}>
                    {cells.action ? (
                      // Always drawn, dimmed until the row is under the pointer,
                      // so it is discoverable without hovering and unmissable
                      // once you are on the row you mean to delete.
                      <div className="flex items-center justify-center gap-1 px-1">
                        <span className="text-[10px] text-[#8b8371]">{r + 1}</span>
                        <span className="opacity-55 group-hover:opacity-100 transition-opacity">
                          {cells.action}
                        </span>
                      </div>
                    ) : r + 1}
                  </th>

                  {cells.map((c, i) => (
                    <td
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      colSpan={c.span}
                      rowSpan={c.rowSpan || 1}
                      className={c.raw ? c.className : `${CELL} ${c.className || ''} ${c.field ? 'p-0' : ''}`}
                    >
                      {c.field ? (
                        <div className="flex items-stretch">
                          <div className="flex-1 min-w-0">
                            <SheetInput {...c.field} readOnly={c.field.readOnly ?? !canEdit} />
                          </div>
                          {c.lock && (
                            <button
                              type="button"
                              onClick={c.lock.onClick}
                              title={c.lock.derived
                                ? 'Kept by the register — click to enter by hand'
                                : 'Entered by hand — click to let the register keep it'}
                              className="px-1 text-[#a39a86] hover:text-[#145f5f] shrink-0"
                            >
                              {c.lock.derived ? <TbLock size={12} /> : <TbLockOpen size={12} />}
                            </button>
                          )}
                        </div>
                      ) : (c.node || c.text || ' ')}
                    </td>
                  ))}

                  {filled < width && <td className={CELL} colSpan={width - filled}>&nbsp;</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
